<div class="trm" data-testid="runner-manuell">
  <section class="trm__order" [attr.aria-label]="'testRunner.orderAria' | translate">
    <h2 class="trm__order-head">
      {{ 'testRunner.order' | translate }}
      @if (orderElements().length > 0) {
        <span class="trm__order-count">· {{ orderElements().length }}</span>
      }
    </h2>

    <ul class="trm__order-list" data-testid="runner-order-list">
      @for (row of orderRows(); track row.element.orderIndex) {
        <li class="trm__element-block">
          <button
            type="button"
            class="trm__element"
            aria-controls="trm-step-detail"
            [class.trm__element--current]="row.isCurrent"
            [class.trm__element--done]="row.isDone"
            [attr.data-testid]="'reihenfolge-item-' + row.ref"
            [attr.aria-current]="row.isCurrent ? 'step' : null"
            [disabled]="!row.runnerCase"
            (click)="selectElement(row)"
          >
            <span class="trm__element-index" aria-hidden="true">
              @if (row.isDone) {
                <mat-icon class="trm__element-check">check</mat-icon>
              } @else {
                {{ row.element.orderIndex + 1 }}
              }
            </span>
            @if (row.isDone) {
              <span class="sr-only" [attr.data-testid]="'element-done-' + row.ref">
                {{ 'testRunner.elementDone' | translate }}
              </span>
            }
            @if (row.element.key) {
              <app-code-id-chip [id]="row.element.key" />
            }
            <span class="trm__element-title">{{ row.element.title }}</span>
            @if (row.hasDataSheet) {
              <app-icon-hint
                class="trm__element-icon"
                icon="table_chart"
                [label]="'testRunner.elementHasDataSheet' | translate"
                [attr.data-testid]="'element-datasheet-' + row.ref"
              />
            }
          </button>

          @if (row.isCurrent) {
            <ng-container [ngTemplateOutlet]="stepList" />
          }
        </li>
      } @empty {
        <li><ng-container [ngTemplateOutlet]="stepList" /></li>
      }
    </ul>
  </section>

  <div class="trm__main">
    @if (activeDataSheet(); as sheet) {
      <section class="trm__sheet" data-testid="runner-datasheet">
        <div class="trm__sheet-head">
          <mat-icon class="trm__sheet-icon" aria-hidden="true">table_chart</mat-icon>
          <span class="trm__sheet-title">{{ 'testRunner.dataSheet' | translate }}</span>
          @if (sheet.key) {
            <app-code-id-chip [id]="sheet.key" />
          }
          <span class="trm__sheet-name">{{ sheet.name }}</span>
          <span class="trm__sheet-count">
            {{ dataSheetRowsKey() | translate: { count: sheet.rows.length } }}
          </span>
        </div>
        <div class="trm__sheet-scroll" data-testid="runner-datasheet-scroll">
          <table class="trm__sheet-table">
            <thead>
              <tr>
                <th scope="col" class="trm__sheet-index">#</th>
                @for (column of sheet.columns; track column) {
                  <th scope="col" class="trm__sheet-var" data-testid="runner-datasheet-column">{{ column }}</th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of sheet.rows; track $index) {
                <tr>
                  <td class="trm__sheet-index">{{ $index + 1 }}</td>
                  @for (column of sheet.columns; track column) {
                    <td>{{ row[column] }}</td>
                  }
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    }

    @if (activePreconditions(); as preconditions) {
      <app-runner-preconditions [text]="preconditions" [open]="preconditionsOpen()" (toggled)="togglePreconditions()" />
    }

    <section
      class="trm__detail"
      id="trm-step-detail"
      [attr.aria-labelledby]="detailLabelledBy()"
      data-testid="step-detail"
    >
      @if (activeStep(); as entry) {
        <header class="trm__detail-head">
          <span class="trm__detail-nr">{{ 'testRunner.step' | translate: { nr: entry.number } }}</span>
          @if (activeGroupTitle(); as groupTitle) {
            <span class="trm__detail-group" data-testid="step-detail-group">· {{ groupTitle }}</span>
          }
          <span class="trm__detail-spacer"></span>
          <app-res-pill
            [status]="pillStatus(entry.step.actualStatus)"
            [label]="resultKey(entry.step.actualStatus) | translate"
          />
        </header>
        <div aria-live="polite">
          @if (!showAction()) {
            <h3 class="trm__detail-title" id="trm-detail-step-title" data-testid="step-detail-title">
              {{ activeTitle() }}
            </h3>
          } @else {
            @if (activeTitle(); as stepTitle) {
              <div class="trm__detail-step-title" id="trm-detail-step-title" data-testid="step-detail-title">
                {{ stepTitle }}
              </div>
            }
            <h3 class="trm__detail-title" id="trm-detail-title">{{ entry.step.description }}</h3>
          }
        </div>

        <h4 class="trm__label">{{ 'testRunner.expected' | translate }}</h4>
        <p class="trm__expected">{{ entry.step.expected || ('testRunner.expectedEmpty' | translate) }}</p>

        @if (activeReferenceUrl(); as referenceUrl) {
          <h4 class="trm__label">{{ 'testRunner.referenceImage.label' | translate }}</h4>
          <img
            #referenceImage
            class="trm__reference"
            data-testid="step-reference-image"
            role="button"
            tabindex="0"
            [src]="referenceUrl"
            [alt]="'testRunner.referenceImage.alt' | translate"
            (click)="lightbox.open(referenceUrl, referenceImage)"
            (keydown.enter)="lightbox.open(referenceUrl, referenceImage)"
          />
        }

        <h4 class="trm__label" id="trm-result-label">{{ 'testRunner.setResultLabel' | translate }}</h4>
        <div class="trm__results" role="group" aria-labelledby="trm-result-label">
          @for (result of results; track result.status) {
            <button
              type="button"
              class="trm__result"
              [attr.data-status]="result.status"
              [class.trm__result--on]="entry.step.actualStatus === result.status"
              [attr.aria-pressed]="entry.step.actualStatus === result.status"
              [attr.data-testid]="'step-detail-status-' + result.status.toLowerCase()"
              [disabled]="readOnly()"
              (click)="setResult(result.status)"
            >
              <mat-icon aria-hidden="true">{{ result.icon }}</mat-icon>
              {{ result.label ?? (setResultKey(result.status) | translate) }}
            </button>
          }
        </div>

        <h4 class="trm__label" id="trm-actual-label">
          {{ 'testRunner.actual' | translate }}
          @if (entry.step.actualStatus === 'FAILED') {
            <span class="trm__required">· {{ 'testRunner.required' | translate }}</span>
          }
        </h4>
        <textarea
          #actualField
          id="trm-actual"
          class="trm__textarea"
          rows="3"
          data-testid="step-actual"
          aria-labelledby="trm-actual-label"
          [class.trm__textarea--invalid]="failNeedsActual()"
          [attr.aria-invalid]="failNeedsActual() ? 'true' : null"
          [attr.aria-describedby]="failNeedsActual() ? 'trm-actual-required' : null"
          [attr.maxlength]="fieldLengths.FREETEXT_MAX"
          [attr.placeholder]="'testRunner.actualPlaceholder' | translate"
          [value]="draft().actualResult"
          (input)="updateActualResult(actualField.value)"
          (blur)="saveActualOnLeave()"
        ></textarea>
        @if (failNeedsActual()) {
          <p
            class="trm__hint trm__hint--error"
            id="trm-actual-required"
            data-testid="step-actual-required"
            role="alert"
          >
            {{ 'testRunner.actualRequired' | translate }}
          </p>
        }

        <h4 class="trm__label" id="trm-note-label">{{ 'testRunner.note' | translate }}</h4>
        <textarea
          #noteField
          class="trm__textarea"
          rows="2"
          data-testid="step-note"
          aria-labelledby="trm-note-label"
          [attr.maxlength]="fieldLengths.FREETEXT_MAX"
          [attr.placeholder]="'testRunner.notePlaceholder' | translate"
          [value]="draft().note"
          (input)="updateNote(noteField.value)"
        ></textarea>
        <div class="trm__save-row">
          <button
            type="button"
            class="trm__secondary"
            data-testid="step-save"
            [disabled]="!activeDirty() || saving()"
            (click)="saveDraft()"
          >
            {{ 'testRunner.save' | translate }}
          </button>
        </div>

        @if (showBugBlock()) {
          @if (isBugLinked()) {
            <div class="trm__bug-linked" data-testid="step-bug-linked">
              <span class="trm__bug-pill">
                <span class="trm__bug-dot" aria-hidden="true"></span>
                <code>{{ linkedBugKey() }}</code>
                {{ 'testRunner.bug.linked' | translate }}
              </span>
              @if (linkedBugUrl(); as bugUrl) {
                <a
                  class="trm__bug-open"
                  data-testid="step-bug-open"
                  target="_blank"
                  rel="noopener noreferrer"
                  [href]="bugUrl"
                >
                  {{ 'testRunner.bug.open' | translate }}
                </a>
              }
              <button
                type="button"
                class="trm__bug-change"
                data-testid="step-bug-change"
                [disabled]="readOnly()"
                (click)="editBugKey()"
              >
                {{ 'testRunner.bug.change' | translate }}
              </button>
            </div>
          } @else {
            <h4 class="trm__label" id="trm-bug-label">{{ 'testRunner.bug.label' | translate }}</h4>
            <div class="trm__bug-row">
              <input
                #bugField
                class="trm__bug-input"
                data-testid="step-bug-key"
                aria-labelledby="trm-bug-label"
                [class.trm__bug-input--invalid]="bugKeyFormatError()"
                [attr.aria-invalid]="bugKeyFormatError() ? 'true' : null"
                [attr.aria-describedby]="bugKeyFormatError() ? 'trm-bug-error' : null"
                [attr.placeholder]="'testRunner.bug.placeholder' | translate"
                [disabled]="readOnly()"
                [value]="bugKeyDraft()"
                (input)="onBugKeyInput(bugField.value)"
                (blur)="saveBugKey()"
              />
              <span class="trm__hint">
                {{ (bugTargetOptions().offersChoice ? 'testRunner.bug.orCreateIn' : 'testRunner.bug.or') | translate }}
              </span>
              @if (bugTarget() || !bugTargetsKnown()) {
                <app-bug-target-picker [targets]="bugTargets()" [locked]="readOnly()" [(choice)]="bugTargetChoice" />
                <button
                  type="button"
                  class="trm__bug-create"
                  data-testid="step-bug-create"
                  [disabled]="readOnly() || creatingBug()"
                  (click)="createBug()"
                >
                  {{ 'testRunner.bug.create' | translate }}
                </button>
              } @else {
                <span class="trm__hint" data-testid="step-bug-no-integration">
                  {{ 'testRunner.bug.noIntegration' | translate }}
                </span>
              }
            </div>
            @if (creatingBug()) {
              <p class="trm__hint" role="status" data-testid="step-bug-creating">
                {{ 'testRunner.bug.creating' | translate }}
              </p>
            }
            @if (bugKeyFormatError()) {
              <p class="trm__hint trm__hint--error" id="trm-bug-error" data-testid="step-bug-error" role="alert">
                {{ 'testRunner.bug.formatError' | translate }}
              </p>
            }
          }
        }

        @if (activeDefects().length > 0 || canCaptureDefect()) {
          <div class="trm__defects">
            @if (activeDefects().length > 0) {
              <ul class="trm__defect-keys" [attr.aria-label]="'defect.capture.keysLabel' | translate">
                @for (defect of activeDefects(); track defect.id) {
                  <li data-testid="defect-key"><app-code-id-chip [id]="defect.key" /></li>
                }
              </ul>
            }
            @if (canCaptureDefect()) {
              <button type="button" class="trm__defect-capture" data-testid="defect-erfassen" (click)="captureDefect()">
                <mat-icon aria-hidden="true">bug_report</mat-icon>
                {{ 'defect.capture.button' | translate }}
              </button>
            }
          </div>
        }

        <h4 class="trm__label">{{ 'testRunner.evidence' | translate }}</h4>
        <app-file-dropzone
          data-testid="step-image-upload"
          accept="image/*"
          [hint]="'testRunner.evidenceHint' | translate"
          (fileSelected)="uploadEvidence($event)"
        />
        @if (uploading()) {
          <p class="trm__hint" role="status" data-testid="step-image-uploading">
            {{ 'testRunner.uploading' | translate }}
          </p>
        }
        <div class="trm__evidence">
          @for (attachment of entry.step.attachments; track attachment.id) {
            <span class="trm__attachment">
              <mat-icon aria-hidden="true">image</mat-icon>
              {{ attachment.originalFilename }}
            </span>
          } @empty {
            <span class="trm__hint">{{ 'testRunner.evidenceEmpty' | translate }}</span>
          }
        </div>

        <h4 class="trm__label" id="trm-comment-label">{{ 'testRunner.comments' | translate }}</h4>
        <ul class="trm__comments">
          @for (comment of entry.step.comments; track comment.id) {
            <li class="trm__comment">
              <app-avatar [name]="comment.authorName ?? ''" [size]="26" />
              <div class="trm__comment-body">
                <div class="trm__comment-meta">
                  <span class="trm__comment-author">{{ comment.authorName }}</span>
                  <span class="trm__comment-when">{{ comment.createdAt | date: 'dd.MM.yyyy HH:mm' }}</span>
                </div>
                <p class="trm__comment-text">{{ comment.body }}</p>
              </div>
            </li>
          } @empty {
            <li class="trm__hint">{{ 'testRunner.commentsEmpty' | translate }}</li>
          }
        </ul>
        <div class="trm__comment-form">
          <textarea
            #commentField
            class="trm__textarea"
            rows="2"
            data-testid="step-comment-input"
            aria-labelledby="trm-comment-label"
            [attr.maxlength]="fieldLengths.FREETEXT_MAX"
            [attr.placeholder]="'testRunner.commentPlaceholder' | translate"
            [value]="commentDraft()"
            (input)="updateComment(commentField.value)"
          ></textarea>
          <button
            type="button"
            class="trm__secondary"
            data-testid="step-comment-submit"
            [disabled]="commentDraft().trim() === ''"
            (click)="addComment()"
          >
            {{ 'testRunner.commentSubmit' | translate }}
          </button>
        </div>
      } @else {
        <p class="trm__hint" data-testid="runner-empty">{{ 'testRunner.empty' | translate }}</p>
      }
      @if (activeCase()) {
        <footer class="trm__footer" data-testid="runner-footer">
          <button
            type="button"
            class="trm__secondary"
            data-testid="step-previous"
            [disabled]="isFirstStep()"
            (click)="goPrevious()"
          >
            <mat-icon aria-hidden="true">chevron_left</mat-icon>
            {{ 'testRunner.previous' | translate }}
          </button>
          <span class="trm__detail-spacer"></span>
          @if (activeStep() && !isLastStep()) {
            <button type="button" class="trm__secondary" data-testid="step-next" (click)="goNext()">
              {{ 'testRunner.next' | translate }}
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          } @else if (nextElementRow()) {
            <button
              type="button"
              class="trm__primary"
              data-testid="step-next-element"
              [class.trm__primary--blocked]="!canGoToNextElement()"
              [attr.aria-disabled]="!canGoToNextElement()"
              [attr.aria-describedby]="canGoToNextElement() ? null : 'trm-next-element-hint'"
              (click)="goToNextElement()"
            >
              {{ 'testRunner.nextElement' | translate: { element: nextElementLabel() } }}
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
            @if (!canGoToNextElement()) {
              <p
                class="trm__hint trm__hint--error trm__footer-hint"
                id="trm-next-element-hint"
                data-testid="step-next-element-hint"
              >
                {{ 'testRunner.nextElementBlocked' | translate }}
              </p>
            }
          } @else if (!readOnly()) {
            <button
              type="button"
              class="trm__primary"
              data-testid="testlauf-abschliessen"
              [class.trm__primary--blocked]="!canComplete()"
              [attr.aria-disabled]="!canComplete()"
              [attr.aria-describedby]="completeBlockedHint() ? 'trm-complete-hint' : null"
              (click)="requestComplete()"
            >
              <mat-icon aria-hidden="true">check</mat-icon>
              {{ 'testRunner.complete' | translate }}
            </button>
            @if (completeBlockedHint(); as hintKey) {
              <p
                class="trm__hint trm__hint--error trm__footer-hint"
                id="trm-complete-hint"
                data-testid="runner-complete-hint"
              >
                {{ hintKey | translate }}
              </p>
            }
          }
        </footer>
      }
    </section>
  </div>
</div>

<ng-template #stepList>
  <ul class="trm__steps">
    @for (row of stepRows(); track row.entry.step.id) {
      <li>
        @if (row.entry.isGroup) {
          <h3 class="trm__group">
            <app-step-dot
              [status]="pillStatus(row.entry.step.actualStatus)"
              [label]="resultKey(row.entry.step.actualStatus) | translate"
            />
            {{ row.entry.step.description }}
          </h3>
        } @else {
          <button
            type="button"
            class="trm__step"
            aria-controls="trm-step-detail"
            [class.trm__step--active]="activeStep()?.step?.id === row.entry.step.id"
            [class.trm__step--child]="row.entry.depth > 0"
            [attr.data-testid]="'step-row-' + row.entry.number"
            [attr.aria-current]="activeStep()?.step?.id === row.entry.step.id ? 'step' : null"
            (click)="selectStep(row.entry)"
          >
            <app-step-dot
              [status]="pillStatus(row.entry.step.actualStatus)"
              [label]="resultKey(row.entry.step.actualStatus) | translate"
              [active]="activeStep()?.step?.id === row.entry.step.id"
            />
            <span class="trm__step-nr">{{ row.entry.number }}</span>
            <span class="trm__step-text">{{ row.entry.step.description }}</span>
            @if (row.hasReference) {
              <app-icon-hint
                class="trm__step-icon"
                icon="photo_library"
                [label]="'testRunner.referenceImage.inList' | translate"
                data-testid="step-reference-icon"
              />
            }
            @if (row.hasEvidence) {
              <app-icon-hint class="trm__step-icon" icon="image" [label]="'testRunner.evidenceInList' | translate" />
            }
          </button>
        }
      </li>
    }
  </ul>
</ng-template>

@if (lightbox.url(); as url) {
  <app-image-lightbox [url]="url" [alt]="'testRunner.referenceImage.alt' | translate" (closed)="lightbox.close()" />
}

<lib-modal
  data-testid="runner-complete-confirm"
  [title]="'testRunner.completeConfirm.title' | translate"
  [text]="'testRunner.completeConfirm.text' | translate: completeSummary()"
  [visible]="confirmCompleteOpen()"
  [saveText]="'testRunner.completeConfirm.confirm' | translate"
  [cancelText]="'testRunner.completeConfirm.cancel' | translate"
  (save)="confirmComplete()"
  (cancel)="cancelComplete()"
  (visibleChange)="!$event && cancelComplete()"
></lib-modal>

<app-defect-erfassen-dialog
  [projectId]="projectId()"
  [stepResultId]="defectStepId()"
  (created)="onDefectCreated($event)"
  (closed)="defectStepId.set(null)"
/>
